import { FC } from 'react';
import styled from 'styled-components';
import { MapData_Base, MapData_ProcessStatus } from '~/common/typescript/models/MapData';
import { Typography, Tooltip, Button } from 'antd';
import MapDataStatusTag from '~/components/app_common/MapDataStatusTag/MapDataStatusTag';
import { UserOutlined, EditOutlined, AimOutlined } from '@ant-design/icons';
import { useSetAtom } from 'jotai';
import { editingMapDataAtom, showMapDataUpdatePanelAtom } from '../../../stores/mapStore';
import { LayerItemUtil } from '../../LegendPanel/LegendTab/LayerItem/LayerItemUtil';
import LayerThumbnail from '../../LegendPanel/LegendTab/LayerItem/LayerThumbnail';

const { Text, Paragraph } = Typography;

interface MapDataItemProps {
  mapData: MapData_Base;
}

const MapDataItem: FC<MapDataItemProps> = ({ mapData }) => {
  const setShowPanel = useSetAtom(showMapDataUpdatePanelAtom);
  const setEditingMapData = useSetAtom(editingMapDataAtom);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingMapData(mapData);
    setShowPanel(true);
  };

  return (
    <UI_MapDataItemWrapper>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
        {/* Layer Thumbnail Preview */}
        <LayerThumbnail data={mapData} />

        {/* Dataset Details */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Text
              strong
              style={{ fontSize: '12px', color: '#0f172a' }}
              ellipsis={{ tooltip: mapData.name }}
            >
              {mapData.name}
            </Text>
            <span
              style={{
                fontSize: '10px',
                color: '#64748b',
                background: '#f1f5f9',
                padding: '0 4px',
                borderRadius: 4,
                lineHeight: '15px',
              }}
            >
              ID: {mapData.id}
            </span>
          </div>

          {mapData.description && (
            <Paragraph
              type="secondary"
              ellipsis={{ rows: 1 }}
              style={{ marginBottom: 0, fontSize: '11px', color: '#64748b' }}
            >
              {mapData.description}
            </Paragraph>
          )}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 1,
              fontSize: '11px',
              color: '#64748b',
            }}
          >
            {mapData.owner && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <UserOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                {mapData.owner.fullname || mapData.owner.username}
              </span>
            )}

            {/* Chỉ hiển thị lỗi khi processStatus là FAILED */}
            {mapData.processStatus === MapData_ProcessStatus.FAILED && mapData.processMessage && (
              <Text type="danger" style={{ fontSize: '10px' }} ellipsis={{ tooltip: mapData.processMessage }}>
                {mapData.processMessage}
              </Text>
            )}
          </div>
        </div>
      </div>

      {/* Status & Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, marginLeft: 6 }}>
        <MapDataStatusTag status={mapData.processStatus as MapData_ProcessStatus} />

        {mapData.processStatus === MapData_ProcessStatus.SUCCESS && (
          <Tooltip title="Phóng tới trên bản đồ">
            <Button
              type="text"
              size="small"
              icon={<AimOutlined style={{ color: '#1677ff', fontSize: 13 }} />}
              onClick={(e) => {
                e.stopPropagation();
                LayerItemUtil.zoomToLayer(mapData);
              }}
              style={{ width: 22, height: 22, padding: 0 }}
            />
          </Tooltip>
        )}

        <Tooltip title="Chỉnh sửa thông tin">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined style={{ color: '#64748b', fontSize: 13 }} />}
            onClick={handleEditClick}
            style={{ width: 22, height: 22, padding: 0 }}
          />
        </Tooltip>
      </div>
    </UI_MapDataItemWrapper>
  );
};

export default MapDataItem;

const UI_MapDataItemWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 6px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.15s ease-in-out;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: #ffffff;
    border-color: #93c5fd;
    box-shadow: 0 1px 3px rgba(37, 99, 235, 0.08);
  }
`;
